import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

function readAuthError(): { error: string; description: string } | null {
  // Supabase appends error params as a query string on some flows and as a
  // URL hash fragment (#error=...&error_description=...) on others.
  const query = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const error = query.get('error') || hash.get('error');
  if (!error) return null;
  const description =
    query.get('error_description') ||
    hash.get('error_description') ||
    query.get('error_code') ||
    hash.get('error_code') ||
    'Something went wrong during sign-in.';
  return { error, description: description.replace(/\+/g, ' ') };
}

export default function CallbackPage() {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const authError = useMemo(readAuthError, []);

  useEffect(() => {
    if (!authError && !loading && isAuthenticated) {
      navigate('/app', { replace: true });
    }
  }, [authError, loading, isAuthenticated, navigate]);

  if (authError) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
        <div className="text-center space-y-4 max-w-md">
          <p className="text-red-400 font-semibold">Sign-in failed</p>
          <p className="text-slate-400">{authError.description}</p>
          <button
            type="button"
            onClick={() => navigate('/login', { replace: true })}
            className="mt-2 px-4 py-2 rounded-lg bg-accent text-slate-950 font-medium hover:opacity-90"
          >
            Back to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center">
      <div className="text-center space-y-4">
        <div className="animate-spin w-8 h-8 border-2 border-accent border-t-transparent rounded-full mx-auto" />
        <p className="text-slate-400">Completing sign-in...</p>
      </div>
    </div>
  );
}
